import { useResponsive } from '../../providers/ResponsiveProvider';
import { __, formatPoints } from '../../utils';
import LoyaltyPoints from './LoyaltyPoints';
import MyReports from './MyReports';
import NewsPreferences from './NewsPreferences';
import NotificationHistory from './NotificationsHistory';
import NotificationSettings from './NotificationsSettings';
import ProfileIdentity from './ProfileIdentity';
import ProfileRail from './ProfileRail';
import ProfileStats from './ProfileStats';
import VotesTokens from './VotesTokens';
import {
  DEFAULT_PROFILE_SECTION,
  profilePath,
  profileSection,
  RAIL_SECTIONS,
  railSectionKey,
  sectionTabs,
  type ProfileSection,
} from './sections';
import { useProfileSummary } from './useProfileSummary';
import { Add } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  PageHeader,
  Stack,
  Tab,
  Tabs,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router';

/**
 * Profil občana - jedna obrazovka s piatimi sekciami, z ktorých prvá
 * a predvolená sú **moje podnety**.
 *
 * Dve rozhodnutia z redizajnu, ktoré vysvetľujú, prečo táto stránka nevyzerá
 * ako ostatné:
 *
 * 1. **Z hlavného menu zmizli „Moje podnety".** Do profilu sa vchádza menovkou
 *    s avatarom vpravo v hlavičke a k vlastným podnetom sa občan dostane buď
 *    odtiaľto, alebo bunkou „Moje" nad mapou na hlavnej stránke.
 * 2. **Hlavičku stránky si kreslí profil sám**, nie shell. Ľavý pás so
 *    sekciami stojí **vedľa** cesty a titulu, nie pod nimi - a shell dáva
 *    `PageHeader` nad celú plochu stránky, takže by pás začínal až pod ním.
 *    `PageHeader` je ten istý komponent z knižnice, aký by nakreslil shell,
 *    len ho sem podáva stránka; route to hlási cez `withoutPageHeader` na
 *    [[RouteMetadata]], aby shell nekreslil hlavičku druhýkrát. Je to jediná
 *    stránka appky s vlastným pásom - inde `setActions` a `RouteMetadata`
 *    platia ďalej ([shell.md](../../../.docs/vyvoj/shell.md)).
 *
 * Sekcia je segment adresy (`/profil/:section`), nie stav komponentu, takže sa
 * dá poslať odkazom a tlačidlo Späť ju vracia.
 */
const ProfilePage: React.FC = () => {
  const { section: sectionKey } = useParams();
  const { isMd } = useResponsive();
  const summary = useProfileSummary();
  const [searchParams] = useSearchParams();

  const section = profileSection(sectionKey);

  // Detail transakcie otvorený z notifikácie o pripísaných bodoch.
  const transactionModalIdParam = searchParams.get('transactionId');
  const transactionModalId = transactionModalIdParam
    ? parseInt(transactionModalIdParam, 10)
    : undefined;

  if (!section) {
    return <Navigate replace to={profilePath(DEFAULT_PROFILE_SECTION)} />;
  }

  /** Tichý údaj vedľa titulu. Len tam, kde ho máme z dát - nie odhad. */
  const meta = (() => {
    switch (section.key) {
      case 'podnety':
        return summary.isLoadingReports
          ? null
          : [
              `${summary.reportsCountLabel} ${__('podaných')}`,
              summary.reportsInProgress > 0 &&
                `${summary.reportsInProgress} ${__('sa vybavuje')}`,
            ]
              .filter(Boolean)
              .join(' · ');
      case 'body':
        return summary.isLoadingPoints
          ? null
          : `${__('Zostatok')} ${formatPoints(summary.points)} ${__('b')}`;
      case 'zetony':
        return summary.isLoadingTokens
          ? null
          : `${formatPoints(summary.availableTokens)} ${__('z')} ${formatPoints(
              summary.totalTokens,
            )} ${__('dostupných')}`;
      default:
        return null;
    }
  })();

  const isReports = section.key === 'podnety';

  const content = (() => {
    switch (section.key) {
      case 'podnety':
        return <MyReports />;
      case 'body':
        return <LoyaltyPoints initModalTransactionId={transactionModalId} />;
      case 'zetony':
        return <VotesTokens />;
      case 'novinky':
        return <NewsPreferences />;
      case 'notifikacie':
        return (
          <SectionTabs section={section}>
            <NotificationHistory />
          </SectionTabs>
        );
      case 'nastavenia':
        return (
          <SectionTabs section={section}>
            <NotificationSettings />
          </SectionTabs>
        );
    }
  })();

  const header = (
    <PageHeader
      root
      title={section.title}
      meta={meta}
      crumbs={[
        { href: '/', label: __('Domov') },
        { href: profilePath(DEFAULT_PROFILE_SECTION), label: __('Profil') },
        { label: section.title },
      ]}
      actions={
        // Identifikátor účtu tu **nie je**. Bol v akciách každej sekcie
        // profilu, čiže na šiestich obrazovkách za sebou, a nikam neviedol -
        // je to údaj na opísanie do podpory, nie akcia stránky.
        isReports ? (
          <Button
            variant='dark'
            size='small'
            startIcon={<Add />}
            component={Link}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...({ to: '/moje-podnety/novy' } as any)} // LinkProps
          >
            {__('Vytvoriť podnet')}
          </Button>
        ) : null
      }
    />
  );

  // Zoznamy vnútri sekcií sa polohujú absolútne, takže karta musí mať výšku.
  // Na mobile jej dáme spodnú hranicu a scrolluje celá stránka.
  //
  // Moje podnety kartu nedostanú: `DataTable` si biely podklad, rám aj
  // stránkovaciu pätičku kreslí sám, takže karta okolo neho bola tabuľka
  // v tabuľke.
  const body = isReports ? (
    content
  ) : (
    <Card
      sx={{
        flex: 1,
        minHeight: { xs: 420, md: 0 },
        p: 0,
        gap: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, p: 2 }}>{content}</Box>
    </Card>
  );

  if (!isMd) {
    return (
      <Stack sx={{ height: '100%', minHeight: 0 }}>
        {header}

        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            gap: 1.5,
            p: 1.5,
            bgcolor: 'background.canvas',
          }}
        >
          {/* Z ľavého pásu je na mobile karta so súhrnom; 300 px vedľa
              obsahu tam nie je kde vziať. */}
          <Card sx={{ flexShrink: 0, gap: 2 }}>
            <ProfileIdentity summary={summary} />
            <ProfileStats summary={summary} />
          </Card>

          <Tabs
            value={railSectionKey(section)}
            variant='scrollable'
            sx={{ flexShrink: 0, minHeight: 0 }}
          >
            {RAIL_SECTIONS.map((item) => (
              <Tab
                key={item.key}
                value={item.key}
                label={item.label}
                component={Link}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                {...({ to: profilePath(item.key) } as any)} // LinkProps
              />
            ))}
          </Tabs>

          {body}
        </Stack>
      </Stack>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <ProfileRail summary={summary} />

      <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        {header}

        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            p: 1.5,
            bgcolor: 'background.canvas',
            // Karta si výšku drží sama a skroluje vnútri; tabuľka rastie
            // s počtom riadkov, takže tá potrebuje skrolovací stĺpec.
            ...(isReports && { overflowY: 'auto' }),
          }}
        >
          {body}
        </Stack>
      </Stack>
    </Box>
  );
};

/**
 * Taby v rámci jednej položky pásu - dnes Nastavenia a História notifikácií.
 * Tab je odkaz na adresu sekcie, nie stav komponentu, rovnako ako sekcie
 * samotné: dá sa poslať a Späť ho vracia.
 */
const SectionTabs: React.FC<
  React.PropsWithChildren<{ section: ProfileSection }>
> = ({ section, children }) => (
  <Stack sx={{ height: '100%', minHeight: 0, gap: 2 }}>
    <Tabs value={section.key} sx={{ flexShrink: 0, minHeight: 0 }}>
      {sectionTabs(railSectionKey(section)).map((tab) => (
        <Tab
          key={tab.key}
          value={tab.key}
          label={tab.tabLabel ?? tab.label}
          component={Link}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          {...({ to: profilePath(tab.key) } as any)} // LinkProps
        />
      ))}
    </Tabs>
    <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
  </Stack>
);

export default ProfilePage;
